import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { type Detection, detectionListSchema } from '../types/detection';

export interface GroupDetectionsParams {
  deviceId: string;
  detectionGroupId: string;
  page?: number;
  pageSize?: number;
}

export interface DetectionsResponse {
  detections: Detection[];
  total: number;
}

export interface UseGroupDetectionsOptions {
  staleTime?: number;
  enabled?: boolean;
}

const fetchGroupDetections = async (params: GroupDetectionsParams): Promise<DetectionsResponse> => {
  const { deviceId, detectionGroupId, page = 1, pageSize = 10 } = params;
  const searchParams = new URLSearchParams();

  searchParams.append('page', String(page));
  searchParams.append('pageSize', String(pageSize));

  const query = searchParams.toString();
  const url = `/devices/${deviceId}/detection-groups/${detectionGroupId}/detections?${query}`;

  try {
    const { data } = await apiClient.get<DetectionsResponse>(url);
    return detectionListSchema.parse(data);
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to fetch detection group entries');
  }
};

export const useGroupDetections = (
  params: GroupDetectionsParams,
  options: UseGroupDetectionsOptions = {},
) => {
  const { staleTime = 0, enabled = true } = options;

  return useQuery({
    queryKey: ['group-detections', params],
    queryFn: () => fetchGroupDetections(params),
    staleTime,
    enabled,
  });
};
